import { useState, useCallback, useRef } from 'react';

/**
 * Hook for showing a brief save indicator (checkmark) after successful field saves.
 *
 * Usage:
 *   const { savedField, markSaved } = useSaveIndicator();
 *
 *   // After successful save:
 *   markSaved('fieldName');
 *
 *   // In JSX:
 *   {savedField === 'fieldName' && <span className="save-indicator">✓</span>}
 *
 * @param {number} duration - How long to show the indicator (ms), default 2000
 * @returns {{ savedField: string|null, markSaved: (fieldName: string) => void }}
 */
export const useSaveIndicator = (duration = 2000) => {
  const [savedField, setSavedField] = useState(null);
  const timeoutRef = useRef(null);

  const markSaved = useCallback(
    fieldName => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      setSavedField(fieldName);

      // Auto-clear after duration
      timeoutRef.current = setTimeout(() => {
        setSavedField(null);
      }, duration);
    },
    [duration],
  );

  return { savedField, markSaved };
};

export default useSaveIndicator;
